/**
 * `/tournament/:id` — matches inside one bench archive.
 *
 * Fetches `tournament.json` and renders a plain list of pairings. Click
 * a match → `/match/:tournamentId/:pairing` (the overlay viewer).
 */
import { useEffect, useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge, Button } from "@pilot-protocol/ui";
import { loadTournament, type EngineTournament } from "../data/bench";

export default function TournamentPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [tournament, setTournament] = useState<EngineTournament | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const t = await loadTournament(id);
        if (!cancelled) setTournament(t);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error) {
    return (
      <div style={pageStyle}>
        <Badge variant="destructive">Failed</Badge>
        <h1 style={titleStyle}>Tournament load error</h1>
        <p style={leadStyle}>
          {error} —{" "}
          <Link to="/tournaments" style={{ color: "var(--foreground)" }}>
            back to tournaments
          </Link>
          .
        </p>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div style={pageStyle}>
        <p style={leadStyle}>Loading…</p>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <nav style={crumbStyle}>
        <Link to="/tournaments" style={crumbLinkStyle}>
          Tournaments
        </Link>
        <span>›</span>
        <span>{id.toUpperCase()}</span>
      </nav>
      <header style={headStyle}>
        <Badge variant="outline">Tournament</Badge>
        <h1 style={titleStyle}>{id}</h1>
        <p style={leadStyle}>
          {tournament.matches.length} matches · {tournament.agents.length} agents ·
          best-of-{tournament.best_of ?? 1}
        </p>
      </header>

      <ul style={listStyle}>
        {tournament.matches.map((m) => {
          const [a, b] = m.pairing.split("_vs_");
          const aWins = m.wins_a;
          const bWins = m.wins_b;
          const winnerA = m.winner === "A";
          const winnerB = m.winner === "B";
          return (
            <li key={m.pairing} style={rowStyle}>
              <div style={rowMainStyle}>
                <p style={rowAgentsStyle}>
                  <span style={winnerA ? winnerStyle : sideStyle}>{a}</span>
                  <span style={vsStyle}>vs</span>
                  <span style={winnerB ? winnerStyle : sideStyle}>{b}</span>
                </p>
                <p style={rowMetaStyle}>
                  {aWins} — {bWins} · {m.reason.replaceAll("_", " ")}
                </p>
              </div>
              <Link
                to={`/match/${id}/${m.pairing}`}
                style={{ textDecoration: "none" }}
              >
                <Button variant="outline" size="sm">
                  Watch ›
                </Button>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const pageStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 24,
};

const crumbStyle: CSSProperties = {
  display: "flex",
  gap: 6,
  alignItems: "center",
  fontFamily: "var(--pp-font-family)",
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
  textTransform: "uppercase",
  letterSpacing: "var(--pp-tracking-caps)",
};

const crumbLinkStyle: CSSProperties = {
  color: "inherit",
  textDecoration: "none",
};

const headStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  alignItems: "flex-start",
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontFamily: "var(--font-display)",
  fontSize: "var(--text-3xl)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const leadStyle: CSSProperties = {
  margin: 0,
  color: "var(--foreground-secondary)",
  fontSize: "var(--text-base)",
};

const listStyle: CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: "none",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  padding: "12px 16px",
  border: "1px solid var(--pp-border)",
  background: "var(--pp-bg-strong)",
};

const rowMainStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  minWidth: 0,
};

const rowAgentsStyle: CSSProperties = {
  margin: 0,
  display: "flex",
  gap: 10,
  alignItems: "baseline",
  fontFamily: "var(--pp-font-family)",
  fontSize: "var(--text-lg)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const sideStyle: CSSProperties = {
  color: "var(--pp-text)",
};

const winnerStyle: CSSProperties = {
  color: "var(--pp-accent-a)",
  fontWeight: "var(--pp-weight-bold)",
};

const vsStyle: CSSProperties = {
  fontSize: "var(--text-xs)",
  color: "var(--foreground-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
};

const rowMetaStyle: CSSProperties = {
  margin: 0,
  fontSize: "var(--text-xs)",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "var(--foreground-muted)",
};
